import React, { useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Card from './Card';
import LocationTypeAhead from './LocationTypeAhead';
import type { StopoffRow, LocationLookupRow } from '../fixtures';
import type { StopoffFieldsPayload } from '../../../services/quote.service';

/** Fields a picked cms.locations row fills on a stopoff draft. Stopoffs have no
 *  location-code column, so nothing is "linked" — it's pure address-fill. */
function locationFill(row: LocationLookupRow): Partial<StopoffFieldsPayload> {
  return {
    stopname: row.lname, stopadd1: row.address1, stopadd2: row.address2,
    stopcity: row.city, stopstate: row.state, stopzip: row.zip,
  };
}

interface Props {
  stopoffs: StopoffRow[];
  /** Empty-state caption (differs between lane and direct contexts). */
  emptyLabel?: string;
  /** When provided the card becomes editable: add (inline form), delete. */
  onAdd?: (payload: StopoffFieldsPayload) => void;
  onUpdate?: (stopId: number, payload: StopoffFieldsPayload) => void;
  onDelete?: (stopId: number) => void;
  /** Whether the quote has a saved ID. When false, clicking "+ Add Stopoff"
   *  shows a "save first" notice instead of opening the add form (stopoffs
   *  apply additional charges and need a saved quote ID). Default true. */
  quoteSaved?: boolean;
}

/** Seed a write-payload draft from a read-row (camelCase → snake field names). */
function rowToDraft(s: StopoffRow): StopoffFieldsPayload {
  return {
    stopnum: s.stopNum, stopname: s.stopName, stoptype: s.stopType,
    stopadd1: s.addr1, stopadd2: s.addr2, stopcity: s.city,
    stopstate: s.state, stopzip: s.zip,
  };
}

/**
 * Stopoffs list shared by the Direct tab (directquote-pinned stops) and the
 * Delivery tab (lane-pinned stops). Mirrors V1's stopoff dialog
 * (inline_quote.pl:2849-3030) with a compact inline add form.
 */
const StopoffsEditor: React.FC<Props> = ({ stopoffs, emptyLabel = 'No stopoffs', onAdd, onUpdate, onDelete, quoteSaved = true }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<StopoffFieldsPayload>({});
  // Inline-edit state: the stopId being edited + its working draft.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<StopoffFieldsPayload>({});
  // Shown only when the user clicks "+ Add Stopoff" on an unsaved (new) quote.
  const [showSaveNotice, setShowSaveNotice] = useState(false);

  const set = (k: keyof StopoffFieldsPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [k]: k === 'stopnum' ? Number(e.target.value) || 0 : e.target.value }));
  const setEdit = (k: keyof StopoffFieldsPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditDraft((d) => ({ ...d, [k]: k === 'stopnum' ? Number(e.target.value) || 0 : e.target.value }));

  const submit = () => {
    if (!onAdd) return;
    // # and Type are both required to add a stopoff.
    if (!draft.stopnum || !draft.stoptype?.trim()) return;
    onAdd({ ...draft });
    setDraft({});
    setAdding(false);
  };

  const beginEdit = (s: StopoffRow) => { setEditingId(s.stopId); setEditDraft(rowToDraft(s)); };
  const saveEdit = (stopId: number) => {
    if (!onUpdate) return;
    onUpdate(stopId, editDraft);
    setEditingId(null);
  };

  const inputCls = 'border border-slate-300 rounded px-1.5 py-1 text-[0.75rem] bg-white text-slate-900 dark:border-gray-600 dark:bg-gray-900 dark:text-slate-100';
  // # and Type are required before a new stopoff can be added.
  const canAdd = !!draft.stopnum && !!draft.stoptype?.trim();

  return (
    <Card
      icon={<ExclamationTriangleIcon className="h-3.5 w-3.5" />}
      title="Stopoffs"
      scaleKey="stopoffs"
      right={
        <div className="flex items-center gap-3">
          {stopoffs.length > 0 && (
            <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{stopoffs.length}</span>
          )}
          <button
            type="button"
            onClick={
              !quoteSaved
                ? () => setShowSaveNotice(true)
                : onAdd
                  ? () => setAdding((v) => !v)
                  : undefined
            }
            className="text-[0.71875rem] font-semibold text-blue-600 hover:underline dark:text-blue-400"
          >
            + Add Stopoff
          </button>
        </div>
      }
      flushBody={stopoffs.length > 0 || adding || (showSaveNotice && !quoteSaved)}
    >
      {showSaveNotice && !quoteSaved && (
        <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2 dark:bg-amber-900/20 dark:border-amber-800">
          <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[0.71875rem] font-semibold text-amber-800 dark:text-amber-300">Save the quote before adding a stopoff</div>
            <div className="text-[0.6875rem] text-amber-800/80 dark:text-amber-300/80 mt-0.5">Stopoffs apply additional charges and require a saved quote ID.</div>
          </div>
          <button type="button" onClick={() => setShowSaveNotice(false)} aria-label="Dismiss" className="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200 flex-shrink-0">
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {adding && onAdd && (
        <div className="px-3 py-2 bg-blue-50/70 border-b border-blue-100 flex flex-col gap-1.5 dark:bg-blue-900/20 dark:border-blue-800">
          <div className="flex flex-wrap gap-1.5">
            <input value={draft.stopnum ? String(draft.stopnum) : ''} onChange={set('stopnum')} placeholder="#" aria-label="Stop number" className={`${inputCls} w-[40px]`} />
            <LocationTypeAhead
              value={draft.stopname ?? ''}
              ariaLabel="Stop name"
              placeholder="Name — search locations…"
              className="flex-1 min-w-0"
              onType={(name) => setDraft((d) => ({ ...d, stopname: name }))}
              onPick={(row) => setDraft((d) => ({ ...d, ...locationFill(row) }))}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <select value={draft.stoptype ?? ''} onChange={(e) => setDraft((d) => ({ ...d, stoptype: e.target.value }))} aria-label="Stop type" className={`${inputCls} w-[150px]`}>
              <option value="">Type…</option>
              <option value="BEFORE DELIVERY">Before Delivery</option>
              <option value="AFTER DELIVERY">After Delivery</option>
            </select>
            <input value={draft.stopadd1 ?? ''} onChange={set('stopadd1')} placeholder="Address" aria-label="Stop address" className={`${inputCls} flex-1`} />
            <input value={draft.stopcity ?? ''} onChange={set('stopcity')} placeholder="City" aria-label="Stop city" className={`${inputCls} w-[110px]`} />
            <input value={draft.stopstate ?? ''} onChange={set('stopstate')} placeholder="ST" aria-label="Stop state" className={`${inputCls} w-[40px]`} />
            <input value={draft.stopzip ?? ''} onChange={set('stopzip')} placeholder="Zip" aria-label="Stop zip" className={`${inputCls} w-[70px]`} />
          </div>
          <div className="flex justify-end items-center gap-1.5">
            {!canAdd && <span className="mr-auto text-[0.6875rem] text-amber-600 dark:text-amber-400">Stop # and Type are required</span>}
            <button type="button" onClick={() => setAdding(false)} className="px-2 py-1 text-[0.6875rem] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">Cancel</button>
            <button type="button" onClick={submit} disabled={!canAdd} className={`px-2 py-1 text-[0.6875rem] font-semibold text-white rounded ${canAdd ? 'bg-blue-600' : 'bg-blue-300 cursor-not-allowed dark:bg-blue-900/50'}`}>Add</button>
          </div>
        </div>
      )}
      {stopoffs.length === 0 && !adding ? (
        <div className="px-3 py-6 text-center text-slate-400 text-[0.75rem] bg-slate-50 border border-dashed border-slate-200 rounded-md dark:text-slate-500 dark:bg-slate-900/40 dark:border-slate-700">
          {emptyLabel}
        </div>
      ) : (
        <div>
          {stopoffs.map((s, i) =>
            editingId === s.stopId ? (
              <div
                key={s.stopId}
                className={`px-3 py-2 bg-blue-50/70 dark:bg-blue-900/20 ${i < stopoffs.length - 1 ? 'border-b border-blue-100 dark:border-blue-800' : ''} flex flex-col gap-1.5`}
              >
                <div className="flex flex-wrap gap-1.5">
                  <input value={editDraft.stopnum ? String(editDraft.stopnum) : ''} onChange={setEdit('stopnum')} placeholder="#" aria-label="Stop number" className={`${inputCls} w-[40px]`} />
                  <LocationTypeAhead
                    value={editDraft.stopname ?? ''}
                    ariaLabel="Stop name"
                    placeholder="Name — search locations…"
                    className="flex-1 min-w-0"
                    onType={(name) => setEditDraft((d) => ({ ...d, stopname: name }))}
                    onPick={(row) => setEditDraft((d) => ({ ...d, ...locationFill(row) }))}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <select value={editDraft.stoptype ?? ''} onChange={(e) => setEditDraft((d) => ({ ...d, stoptype: e.target.value }))} aria-label="Stop type" className={`${inputCls} w-[150px]`}>
                    <option value="">Type…</option>
                    <option value="BEFORE DELIVERY">Before Delivery</option>
                    <option value="AFTER DELIVERY">After Delivery</option>
                  </select>
                  <input value={editDraft.stopadd1 ?? ''} onChange={setEdit('stopadd1')} placeholder="Address" aria-label="Stop address" className={`${inputCls} flex-1`} />
                  <input value={editDraft.stopcity ?? ''} onChange={setEdit('stopcity')} placeholder="City" aria-label="Stop city" className={`${inputCls} w-[110px]`} />
                  <input value={editDraft.stopstate ?? ''} onChange={setEdit('stopstate')} placeholder="ST" aria-label="Stop state" className={`${inputCls} w-[40px]`} />
                  <input value={editDraft.stopzip ?? ''} onChange={setEdit('stopzip')} placeholder="Zip" aria-label="Stop zip" className={`${inputCls} w-[70px]`} />
                </div>
                <div className="flex justify-end gap-1.5">
                  <button type="button" onClick={() => setEditingId(null)} className="px-2 py-1 text-[0.6875rem] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">Cancel</button>
                  <button type="button" onClick={() => saveEdit(s.stopId)} aria-label="Save stop" className="px-2 py-1 text-[0.6875rem] font-semibold text-white bg-blue-600 rounded">Save</button>
                </div>
              </div>
            ) : (
              <div
                key={s.stopId}
                className={`px-3 py-2 ${i < stopoffs.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''} flex items-start gap-3`}
              >
                <span className="text-[0.6875rem] text-slate-400 mt-0.5 dark:text-slate-500">{s.stopNum}.</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.75rem] font-semibold text-slate-900 dark:text-slate-100">{s.stopName || '—'}</div>
                  <div className="text-[0.71875rem] text-slate-600 dark:text-slate-200">
                    {[s.addr1, s.city, s.state, s.zip].filter(Boolean).join(', ') || '—'}
                  </div>
                </div>
                {s.stopType && (
                  <span className="text-[0.625rem] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-px rounded uppercase tracking-wider dark:text-slate-300 dark:bg-slate-700 dark:border-slate-600">
                    {s.stopType}
                  </span>
                )}
                {onUpdate && (
                  <button
                    type="button"
                    onClick={() => beginEdit(s)}
                    aria-label={`Edit stop ${s.stopNum}`}
                    className="text-[0.6875rem] font-semibold text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(s.stopId)}
                    aria-label="Remove stopoff"
                    className="w-5 h-5 inline-flex items-center justify-center text-slate-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ),
          )}
        </div>
      )}
    </Card>
  );
};

export default StopoffsEditor;
